"use client";

import { getPostHogEventContext } from "@/lib/posthog-browser";
import { POSTHOG_EVENT_CONTEXT_FORM_FIELD } from "@/lib/posthog-constants";

import { useLocale } from "next-intl";
import { useRef, useTransition } from "react";
import { Sparkles, Loader2 } from "lucide-react";
import { useCart } from "@/ui/components/cart/cart-context";
import { usePdpVariantNavigation } from "./variant-navigation-context";
import { addPdpLineToCheckout } from "./add-to-cart-action";

interface BundleSaveButtonProps {
	channel: string;
	variantId: string;
	productName: string;
	variantName: string;
	disabled?: boolean;
}

/**
 * "+2 nehmen — 5% Bundle-Rabatt" CTA on PDP.
 * Adds 2 of current variant in one click + signals downstream 5% voucher (BUNDLE5).
 */
export function BundleSaveButton({
	channel,
	variantId,
	productName,
	variantName,
	disabled = false,
}: BundleSaveButtonProps) {
	const locale = useLocale();
	const navigation = usePdpVariantNavigation();
	const submitting = useRef(false);
	const [isPending, startTransition] = useTransition();
	const { openCart } = useCart();

	const handleClick = () => {
		if (disabled || isPending || submitting.current || !variantId) return;
		if (navigation && !navigation.canSubmitVariant(variantId)) return;
		submitting.current = true;
		const formData = new FormData();
		formData.set(POSTHOG_EVENT_CONTEXT_FORM_FIELD, JSON.stringify(getPostHogEventContext()));
		formData.set("channel", channel);
		formData.set("locale", locale);
		formData.set("variantId", variantId);
		formData.set("quantity", "2");
		formData.set("productName", productName);
		formData.set("variantName", variantName);

		startTransition(async () => {
			try {
				const result = await addPdpLineToCheckout(formData);
				if (result.success) openCart?.();
			} finally {
				submitting.current = false;
			}
		});
	};

	return (
		<button
			type="button"
			onClick={handleClick}
			disabled={disabled || isPending || navigation?.pending}
			aria-busy={isPending || navigation?.pending || undefined}
			className="bg-success/10 border-success/30 hover:bg-success/20 hover:border-success/50 group relative flex w-full items-center justify-between gap-3 rounded-2xl border-2 border-dashed px-4 py-3.5 text-success transition-all disabled:opacity-50"
		>
			<div className="flex items-center gap-2.5">
				<div className="bg-success/15 flex h-9 w-9 shrink-0 items-center justify-center rounded-full">
					{isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
				</div>
				<div className="flex flex-col items-start text-left">
					<span className="font-mono text-[0.62rem] font-semibold uppercase tracking-[0.18em]">
						+2 nehmen
					</span>
					<span className="font-display text-[0.85rem] font-medium leading-tight tracking-[-0.005em]">
						Spare 5% auf den ganzen Warenkorb
					</span>
				</div>
			</div>
			<span className="inline-flex h-8 shrink-0 items-center rounded-full bg-success px-3 font-mono text-[0.65rem] font-bold uppercase tracking-[0.16em] text-success-foreground shadow-sm">
				-5%
			</span>
		</button>
	);
}
